<script setup lang="tsx">
import type { ListboxItemSelectEvent } from 'reka-ui'

import {
  ComboboxItem,
} from 'reka-ui'

import type { SearchResultType } from '~/types'
import type { ResponseTypeGetGlobal } from '~/composables/api/search/useFetchSearch'

import IconTemplateGray from '~/assets/images/icon-template-gray.svg?url'

type SearchResult = ResponseTypeGetGlobal['data'][number]

const props = defineProps<{
  item: SearchResult
}>()

const emit = defineEmits<{
  open: [item: { type: SearchResultType, id: string }, target?: '_blank']
}>()

const { formatDate } = useIntl()

const document = computed(() => isDocument(props.item) ? props.item : undefined)
const folder = computed(() => isFolder(props.item) ? props.item : undefined)

const displayName = computed(() => {
  if (isDocument(props.item))
    return props.item.title

  return props.item.name
})

const iconOverlayText = computed(() => {
  if (!isDocument(props.item))
    return

  return getDocumentsRowIconOverlayText(props.item)
})

const rowIcon = computed(() => {
  if (isFolder(props.item) || isDocument(props.item))
    return getDocumentsRowIcon(props.item)

  return IconTemplateGray
})

const formattedDocumentCreatedAtDate = computed(() =>
  document.value && document.value.created_at
    ? formatDate(new Date(document.value.created_at))
    : undefined,
)

const handleSelect = (event: ListboxItemSelectEvent<SearchResult>) => {
  if (!event.detail.value)
    return

  const originalEvent = event.detail.originalEvent
  emit('open', { type: event.detail.value._type, id: event.detail.value.id }, (originalEvent?.metaKey || originalEvent?.ctrlKey) ? '_blank' : undefined)
}
</script>

<template>
  <ComboboxItem
    :value="item"
    class="flex w-full items-center justify-between gap-2 rounded-md px-4 py-2 text-sm text-gray-600 data-[highlighted]:bg-gray-50"
    @select.prevent="handleSelect"
  >
    <div class="flex w-full flex-1 items-center justify-start gap-2">
      <div class="relative size-7 shrink-0">
        <img
          :src="rowIcon"
          class="absolute size-64 max-w-none origin-top-left scale-[.1]"
        >
        <span
          v-if="document && iconOverlayText"
          class="absolute inset-0 flex items-center justify-center"
        >
          <span
            class="
                inline-block
                max-w-full
                items-center
                overflow-hidden
                text-clip
                bg-gray-800/40
                px-0.5
                text-[8px]
                leading-[10px]
                text-white
                "
          >
            {{ iconOverlayText }}
          </span>
        </span>
      </div>
      <span class="grow select-none">
        <span
          v-if="document || folder"
          class="inline-flex w-full grow flex-col"
        >
          <span class="line-clamp-1 break-all text-sm">{{ displayName }}</span>
          <DocumentsBreadcrumbs
            :breadcrumbs="(document ?? folder)!.breadcrumb"
            class="text-xs text-gray-500"
          />
        </span>
        <span
          v-else
          class="text-sm"
        >{{ displayName }}</span>
      </span>
    </div>

    <div
      v-if="document"
      class="flex items-center gap-3"
    >
      <span class="text-xs">{{ formattedDocumentCreatedAtDate }}</span>
      <div
        v-if="document.owner"
        class="flex w-4 items-center justify-end"
      >
        <DTAvatar
          size="xxs"
          :name="document.owner.display_name"
          variant="colored"
          class="[&>div]:!static"
        />
      </div>
    </div>
  </ComboboxItem>
</template>
